
import * as Sentry from "@sentry/vue";
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import CollapsibleOption from "~/components/form/CollapsibleOption.vue";
import LargeSelect from "~/components/form/LargeSelect.vue";
import orderSummary from "~/components/ship/orderSummary.vue";
import Validation from "~/entities/Validation";
import ValidationContents from "~/entities/ValidationModules/ValidationContents";

export default Vue.extend({
  name: "ShipContents",
  layout: "WithServices",
  components: { LargeSelect, CollapsibleOption, orderSummary },
  data() {
    return {
      stepNumber: 4,
      validation: new Validation(new ValidationContents()),
      isOpen: false,
      isOpenResponsive: false,
      rendered: false,
      boxesDescriptions: [] as any,
    };
  },
  computed: {
    // Vuex getters
    ...mapGetters({
      activeNotices: "errorsBag/activeNotices" as any,
      alertResponses: "errorsBag/alertResponses" as any,
      loading: "loading",
      boxes: "ship/boxes",
      trips: "ship/trips",
      purpose: "ship/purpose",
      rates: "ship/rates",
      units: "ship/units",
      descriptions: "ship/descriptions",
      selectedRateSpeed: "ship/selectedRateSpeed",
      selectedRateSpeedIndex: "ship/selectedRateSpeedIndex",
      premiumProtection: "ship/premiumProtection",
      subTotal: "payment/subTotal",
      currentEvent: "eventShipping/currentEvent",
    }),
  },
  mounted() {
    if ((this as any).descriptions && (this as any).descriptions.length) {
      for (let index = 0; index < (this as any).descriptions.length; index++) {
        (this as any).boxesDescriptions[index] = [] as any;
        for (
          let step = 0;
          step < (this as any).descriptions[index].length;
          step++
        ) {
          if ((this as any).descriptions[index][step]) {
            (this as any).boxesDescriptions[index][step] = {
              outsideDescription: (this as any).descriptions[index][step]
                .outsideDescription,
              insideDescription: (this as any).descriptions[index][step]
                .insideDescription,
            };
          } else {
            (this as any).boxesDescriptions[index][step] = {
              outsideDescription:
                (this as any).boxes &&
                (this as any).boxes[step].outsideDescription
                  ? (this as any).boxes[step].outsideDescription
                  : "",
              insideDescription:
                (this as any).boxes &&
                (this as any).boxes[step].insideDescription
                  ? (this as any).boxes[step].insideDescription
                  : "",
            };
          }
        }
      }
    } else {
      for (let index = 0; index < (this as any).trips.length; index++) {
        (this as any).boxesDescriptions[index] = [] as any;
        for (let step = 0; step < (this as any).boxes.length; step++) {
          (this as any).boxesDescriptions[index][step] = {
            outsideDescription: (this as any).boxes[step].outsideDescription
              ? (this as any).boxes[step].outsideDescription
              : "",
            insideDescription: (this as any).boxes[step].insideDescription
              ? (this as any).boxes[step].insideDescription
              : "",
          };
        }
      }
    }
    (this as any).validation.module.component = this;
    (this as any).setResidentialFees(); // here?
    (this as any).calculateSubTotal(); //recalculate this in case of avs having the residential flag
    (this as any).rendered = true;
    try {
      Sentry.addBreadcrumb({
        message: "Threshold: contents.",
        data: {
          selectedRateSpeed: JSON.stringify((this as any).selectedRateSpeed),
          subtotal: (this as any).subTotal,
        },
        category: "contents",
      });
    } catch (e) {
      console.log(e);
    }
    this.updateTripsFormattedAddressLabels();
    window.scrollTo(0, 0);
    if (typeof window !== "undefined" && window.gtag) {
      let route = this.$route.name;
      let event = null;
      if (
        route === "events-event" ||
        route === "event" ||
        route === "partners-event"
      ) {
        event = this.currentEvent?.name
          ? this.currentEvent.name
          : this.$route.params.event;
      }
      try {
        window.gtag("event", "page_impression", {
          pagePath: this.$route.path,
          pageTitle: event
            ? "Ship - " + event + " - Contents"
            : "Ship - Contents",
          visitorType: this.$auth.loggedIn ? "customer" : "guest",
        });
      } catch (e) {
        console.log(e);
      }
    }
  },
  methods: {
    // Vuex actions
    ...mapMutations({
      setLoading: "setLoading",
      setBoxes: "ship/setBoxes",
      setTrips: "ship/setTrips",
      setUnits: "ship/setUnits",
      setPurpose: "ship/setPurpose",
      setShipMode: "ship/setShipMode",
      setDescriptions: "ship/setDescriptions",

      setResidentialFees: "ship/setResidentialFees",
      updateTripsFormattedAddressLabels:
        "ship/updateTripsFormattedAddressLabels",
    }),
    ...mapActions({
      getRates: "ship/getRates",
      calculateSubTotal: "payment/calculateSubTotal",
      requestAlert: "errorsBag/requestAlert",
      clearActiveNotices: "errorsBag/clearActiveNotices",
      clearAlertResponses: "errorsBag/clearAlertResponses",
      removeNotice: "errorsBag/removeNotice",
      addNotice: "errorsBag/addNotice",
      setStep: "ship/setStep",
      saveTemporalOrder: "ship/saveTemporalOrder",
      logRequest: "ship/logRequest",
    }),
    updateRates(value: any) {
      //modify rates here. (needs to be completed)
      (this as any).setRates(value);
    },
    dateToISO(date: any) {
      if (typeof date === "string" || date instanceof String) {
        date = new Date(date as string);
      }
      if (date instanceof Date) {
        let offsetMs = date.getTimezoneOffset() * 60 * 1000;
        let msLocal = date.getTime() - offsetMs;
        let dateLocal = new Date(msLocal);
        let iso = dateLocal.toISOString();
        let isoLocal = iso.slice(0, 19);
        return isoLocal;
      }
      return false;
    },
    isBeforeToday(date: any) {
      if (typeof date === "string" || date instanceof String) {
        date = new Date(date as string);
      }
      let formattedToday = new Date(
        new Date(new Date().setHours(0, 0, 0, 0)).setUTCHours(0)
      ).toISOString();
      let today = new Date(formattedToday as string);
      return date < today;
    },
    formatPrices(value: any) {
      return Number(parseFloat(value as string).toFixed(2)).toLocaleString(
        "en-US",
        { maximumFractionDigits: 2, minimumFractionDigits: 2 }
      );
    },
    formatDate(date: any, daysDelta = 0) {
      if (typeof date === "string" || date instanceof String) {
        date = new Date(date as string);
      }
      if (daysDelta) {
        date.setDate(date.getDate() + daysDelta);
      }
      let formattedDate = date.toLocaleDateString("en-us", {
        year: "numeric",
        weekday: "short",
        month: "short",
        day: "numeric",
        timeZone: "utc",
      });
      return formattedDate;
    },
    async validateStep() {
      if (await (this as any).validation.Validate()) {
        (this as any).setDescriptions((this as any).boxesDescriptions);
        (this as any).setLoading(true);
        let orderSaved = await (this as any).saveTemporalOrder();
        if (orderSaved === 403) {
          this.validation.module.requestAlert({
            event: "blocked",
            type: "order",
          });
          (this as any).setLoading(false);
        } else if (orderSaved) {
          (this as any).logRequest(window ? window.navigator.userAgent : "");
          (this as any).setStep(5);
        } else {
          this.validation.module.requestAlert({
            event: "failedSave",
            type: "order",
          });
          (this as any).setLoading(false);
        }
      } else {
        (this as any).setLoading(false);
      }
    },
  },
});
